<template>
    <div
        v-if="props.status === 'loaded' || props.status === 'loading'"
        :class="$style.results"
    >
        <SearchCards
            :cards="gamesSearched"
            :searchQuery="props.searchResults?.queryTerm"
            :onClick="props.onClick"
        />
        <SearchCards
            :cards="groupsSearched"
            :onClick="props.onClick"
        />
    </div>
    <div
        v-else-if="props.status === 'empty'"
        :class="$style.empty"
    >
        <div :class="$style.empty_wrapper_text">
            <div :class="$style.no_results_img_wrapper">
                <img
                    :src="EyesImg"
                    alt="no_results"
                    loading="lazy"
                    :class="$style.no_results_img"
                />
            </div>
            <div :class="$style.no_results_text">
                <Typography
                    type="header"
                    size="s"
                    :responsive="false"
                >
                    {{ $tr('search.empty_title') }}
                </Typography>
                <Typography
                    size="s"
                    :responsive="false"
                    :class="$style.no_results_desc"
                >
                    {{ $tr('search.empty_desc') }}
                </Typography>
            </div>
        </div>
        <div :class="$style.sep_section" />
        <Typography
            type="caption"
            size="all-caps"
            :class="$style.title"
        >
            {{ $tr('search.top_games') }}
        </Typography>
        <SearchCards :cards="topGames" />
    </div>
    <div
        v-else
        :class="$style.initial"
    >
        <PromoCategoriesSwitchers />
        <Typography
            type="caption"
            size="all-caps"
            :class="$style.title"
        >
            {{ $tr('search.top_games') }}
        </Typography>
        <SearchCards :cards="topGames" />
        <template v-if="recentlyPlayedGames?.length">
            <Typography
                type="caption"
                size="all-caps"
                :class="$style.title"
            >
                {{ $tr('search.recent_games') }}
            </Typography>
            <SearchCards :cards="recentlyPlayedGames" />
        </template>
    </div>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import type { IconNameType } from '@/components/Icon/Icon.vue'
import EyesImg from '@/components/images/eyes.webp'
import Typography from '@/components/Typography.vue'
import { useTrendingData } from '@/query/use-trending-data'
import { ROUTE_NAMES, type ROUTE_NAMES_GAME_GROUPS } from '@/router/constants'
import { useMyGamesStore } from '@/store/my-games/my-games-store'

import PromoCategoriesSwitchers from '../components/PromoCategoriesSwitchers.vue'
import SearchCards from '../components/SearchCards.vue'
import type { SearchResults, SearchStatusResult } from '../types'

const props = defineProps<{
    searchResults: SearchResults | undefined
    status: SearchStatusResult
    onClick: () => void
}>()

const FIRST_RECENT_GAMES = 3
const FIRST_TOP_GAMES = 3
const MAP_GROUPS_LINK_TO_ICON: Record<ROUTE_NAMES_GAME_GROUPS, IconNameType> = {
    [ROUTE_NAMES.CATEGORY]: 'all_games',
    [ROUTE_NAMES.DEVELOPER]: 'all_games',
    [ROUTE_NAMES.SERIES]: 'all_series',
    [ROUTE_NAMES.TAG]: 'all_tags',
    [ROUTE_NAMES.COLLECTION]: 'all_games',
}

const myGamesStore = useMyGamesStore()

const { myUnzippedGames } = storeToRefs(myGamesStore)
const { unzippedTrendingWithGames } = useTrendingData({})

const recentlyPlayedGames = computed(() => myUnzippedGames.value.slice(0, FIRST_RECENT_GAMES))
const topGames = computed(() => unzippedTrendingWithGames.value?.slice(0, FIRST_TOP_GAMES))

const gamesSearched = computed(() =>
    props.searchResults?.result
        ?.filter((item) => item.routeName === ROUTE_NAMES.GAME_PAGE)
        .map((item) => ({ hru: item.gameHru, preview: item.image, title: item.name, routeName: item.routeName })),
)
const groupsSearched = computed(() =>
    props.searchResults?.result
        ?.filter((item) => item.routeName !== ROUTE_NAMES.GAME_PAGE)
        .map((item) => ({
            hru: item.hru,
            title: item.name,
            routeName: item.routeName,
            previewIcon: MAP_GROUPS_LINK_TO_ICON[item.routeName],
        })),
)
</script>
<style module>
.results {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.title {
    color: var(--carbon-600);
}

.initial {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
}

.empty {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 16px auto auto;
    width: 100%;
}

.no_results_text {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-left: 12px;
    margin-bottom: 8px;
}

.no_results_desc {
    color: var(--white-60);
}

.no_results_img_wrapper {
    width: 100px;
    text-align: center;
    flex-shrink: 0;
}

.no_results_img {
    width: 60px;
    height: 60px;
}

.empty_wrapper_text {
    display: flex;
    align-items: center;
}

.sep_section {
    width: 100vw;
    height: 1px;
    background-color: var(--white-12);
    margin: 12px 0 12px calc(calc((var(--global-padding-horizontal) + var(--pg-safe-area-left))) * -1);
}

@media (--desktop-small-plus) {
    .initial {
        gap: 12px;
    }
    .empty {
        margin: 0;
    }
    .results {
        gap: 8px;
    }

    .sep_section {
        margin: 12px 0;
        width: 100%;
    }
}
</style>
